import { Row } from '../../interfaces/gridInterfaces';
import { addMultipleRowsHelper, addMultipleRowsWithContentHelper, applyTransaction, collapseAboveNode, expandAboveNode, getSelectedRowIndexes, getSmallestSelectedIndex, getUniqueChildrenArray } from '../../helper/helperFunctions';
import { parentRowsAferRollup } from '../../helper/helpersSubModule';
import { context, trace } from '@opentelemetry/api';
import { logSpan } from '../../../../helpers/loggingHelper';
import { populateBacklogStatusColumnIfNeeded } from '../../helper/cellValueChangeHelpers';
import { formatInsertUndoInfoForLogs } from '../../../../helpers/loggerFormatHelper';

export interface InsertMultipleRowsCoreOptions {
  above: boolean;
  [key: string]: any;
}

export interface InsertMultipleRowsCoreResult {
  addIndex: number;
  newRows: Row[];
  smallestRowIndexSelected: number;
  currentSheetRows: Row[];
  undoInfo: any;
}

// Type for gridRef (minimal, can be improved with ag-Grid types)
interface GridRef {
  current: {
    api: any;
    [key: string]: any;
  };
}

// insertRow: callback that returns a row template (type any for now)
export async function insertMultipleRowsCore(
  gridRef: GridRef,
  insertRow: (rowProps: any, cb: (row: Row) => void) => void,
  numOfRows: number,
  options: InsertMultipleRowsCoreOptions,
  cellIndex: number,
  aboveNode: any,
  selectedColumnId:string,
  focusRowAfterOperation? : (addIndex:number) => void,
  alreadyAddedRows?:Row[],
  isRowsInsertedForPaste?:boolean,
  partialUndoRef?:any
): Promise<InsertMultipleRowsCoreResult | null> {
  const activeSpan = trace.getSpan(context.active());
  let functionFlow = ["insertMultipleRowsCore started"];
  const { above } = options;
  let currentSheetRows = gridRef.current?.api.getModel().rowsToDisplay;
  let updatedRowIndex = cellIndex;
  let nodeBelow = currentSheetRows[cellIndex] ?? null
  let aboveNodeCollapsed = false;

if (above === true) {
  // check the row above
  aboveNodeCollapsed = (currentSheetRows[cellIndex - 1]?.allChildrenCount && 
                        currentSheetRows[cellIndex - 1].expanded === false) 
                        ? currentSheetRows[cellIndex - 1].id 
                        : false;
} else {
  // check the current row itself
  aboveNodeCollapsed = (currentSheetRows[cellIndex]?.allChildrenCount && 
                        currentSheetRows[cellIndex].expanded === false) 
                        ? currentSheetRows[cellIndex].id 
                        : false;
}
  let updatedCellIndex = cellIndex;
  let nodeStateMap = aboveNodeCollapsed ? expandAboveNode(above?currentSheetRows[cellIndex - 1]:currentSheetRows[cellIndex ], gridRef) : "";
  // Update the row index based on allChildrenCount
  return new Promise((resolve) => {
      setTimeout(() => {
          functionFlow.push("Inside setTimeout")
          let currentSheetRows = gridRef.current?.api.getModel().rowsToDisplay;
          if (aboveNodeCollapsed) {
            functionFlow.push("aboveNodeCollapsed before expand");
            if(nodeBelow){
            nodeBelow = gridRef.current.api.getRowNode(nodeBelow.id);
            // Set the new focus to the updated row index
            gridRef.current.api.setFocusedCell(nodeBelow.rowIndex, selectedColumnId || 'ag-Grid-AutoColumn');

            updatedCellIndex = gridRef.current.api.getFocusedCell()?.rowIndex || cellIndex;}
            else{
              updatedCellIndex = currentSheetRows.length;
            }
          } else {
            // cellIndex is a sourceRowIndex; convert to displayed index for rowsToDisplay operations
            updatedCellIndex = gridRef.current.api.getFocusedCell()?.rowIndex ?? cellIndex;
          }
          let belowAddIndex;
          let selectedNodes = gridRef.current.api.getSelectedNodes() || gridRef?.current?.api?.getDisplayedRowAtIndex(currentSheetRows.length - 1);
          let cellRanges = gridRef.current.api.getCellRanges();
          let cell = updatedCellIndex!=null ? updatedCellIndex : gridRef.current.api.getFocusedCell().rowIndex || gridRef?.current?.api?.getDisplayedRowAtIndex(currentSheetRows.length - 1).rowIndex;
          if (selectedNodes.length === 0) {
              if (cell!=null && currentSheetRows[cell]) selectedNodes = [{ data: currentSheetRows[cell].data, rowIndex: cell }];
          }
          let selectedRowIndexes = selectedNodes.length ? getSelectedRowIndexes(gridRef) :[];
          if (selectedRowIndexes.length === 0 && selectedNodes[0]) {
              selectedRowIndexes = [selectedNodes[0].rowIndex];
          }
           let selectedNode = currentSheetRows[selectedNodes[0].rowIndex];
              const childrenArray = getUniqueChildrenArray(selectedNode);
              const totalChildrenCount = childrenArray?.filter(node => node.rowIndex !== null).length;
          if (!above) {
            functionFlow.push("Inside !above")
             

              if (childrenArray.length === 1) {
                  belowAddIndex = selectedNode.rowIndex + 1;
              } else if (selectedNode.expanded && aboveNodeCollapsed) {
                  belowAddIndex = selectedNode.allChildrenCount + selectedNode.rowIndex + 1;
              } else {
                  belowAddIndex = selectedNode.rowIndex + 1;
              }
          }
          let smallestRowIndexSelected = selectedRowIndexes.length ? getSmallestSelectedIndex(selectedRowIndexes):-1;
        let rawId = currentSheetRows[smallestRowIndexSelected]?.data.l_id ?? "";

        let prefixlid = (!above && childrenArray.length>1 && !aboveNodeCollapsed)
          ? rawId
          : rawId.split(".").slice(0, -1).join(".");
            let newRows = alreadyAddedRows ? addMultipleRowsWithContentHelper(alreadyAddedRows) : addMultipleRowsHelper(insertRow, numOfRows, prefixlid);
          if (newRows && newRows.length) {
              let temp = gridRef.current.api.applyTransaction({
                  add: [...newRows],
                  addIndex: above ? smallestRowIndexSelected : belowAddIndex
              });
              newRows.map((row) => {
                if (row.l_id && row.l_id.split('.').length > 1) {
                    populateBacklogStatusColumnIfNeeded(row, '', true, '');
                }
            });
            let parentRowsAffectedByRollup: any[] = [];
            let additionalParentAndExtraRowsForUndo: any[] = []
             if (!isRowsInsertedForPaste) {
              const result = parentRowsAferRollup(newRows, gridRef, '', '');
              parentRowsAffectedByRollup = result.parentRowsAffectedByRollup;
              additionalParentAndExtraRowsForUndo = result.additionalParentAndExtraRowsForUndo || [];
              let mergedRowsAfterRollupAndPredecessorChange = result.mergedRowsAfterRollupAndPredecessorChange;
              if (parentRowsAffectedByRollup.length) {
                applyTransaction(gridRef, parentRowsAffectedByRollup);
              }
             }
            // Explicitly type undoStackData to fix type error
            if (aboveNodeCollapsed) {
              let nodeToExpand = gridRef.current?.api.getRowNode(aboveNodeCollapsed);
                collapseAboveNode(nodeToExpand, gridRef, nodeStateMap)
              } 
              let undoStackData: { type: string; action: string; index: any }[] = [];
              for (let i = 0; i < newRows.length; i++) {
                  undoStackData.push({ type: 'row', action: 'insert', index: temp.add[0].rowIndex + i });
              }
            let belowNodeId = ""
            if (above) {
              belowNodeId = currentSheetRows[smallestRowIndexSelected]?.id || 'none'
            }
            else if (!above) {
              belowNodeId = currentSheetRows[belowAddIndex]?.id || 'none'
            }
            else {belowNodeId = 'none'}
            let undoInfo
               undoInfo = {
                  type: 'row',
                  action: 'insertMultiple',
                  allRowsAdded: newRows,
                  addedRows: undoStackData,
                  updatedRows: parentRowsAffectedByRollup,
                  undoUpdatedRows: additionalParentAndExtraRowsForUndo,
                  aboveNode: aboveNode,
                  belowNodeId: belowNodeId,
                  above:above
              }
              if(partialUndoRef) partialUndoRef.current=undoInfo

              logSpan("insertMultipleRowsCore-undoInfo", activeSpan?.spanContext(),"INFO", formatInsertUndoInfoForLogs(undoInfo));
              resolve({ addIndex: temp.add[0].rowIndex, newRows, smallestRowIndexSelected, currentSheetRows, undoInfo });
              setTimeout(()=>{
                if(focusRowAfterOperation) focusRowAfterOperation(temp.add[0].rowIndex)
              },100)
              functionFlow.push("insertMultipleRowsCore end")
              activeSpan?.setAttribute("insertMultipleRowsCore-functionFlow", functionFlow);
              return;
            }
          functionFlow.push("insertMultipleRowsCore end null resolve")
          activeSpan?.setAttribute("insertMultipleRowsCore-functionFlow", functionFlow);
          resolve(null);
      }, 300)
  });
}
